import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/features/auth/store'
import { listUsers } from '@/features/auth/api'
import {
  LOCATION_STATUSES,
  LOCATION_TYPES,
  LOCATION_TYPE_LABELS,
  createLocation,
  updateLocation,
  type StockLocation,
} from '@/features/inventory/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const schema = z.object({
  code: z.string().trim().min(1, 'Enter a code').max(50),
  name: z.string().trim().min(1, 'Enter a name').max(100),
  type: z.enum(LOCATION_TYPES),
  status: z.enum(LOCATION_STATUSES),
  address: z.string().trim().max(500),
  city: z.string().trim().max(100),
  state: z.string().trim().max(100),
  postal_code: z.string().trim().max(20),
  phone: z.string().trim().max(30),
  latitude: z.string().trim(),
  longitude: z.string().trim(),
  manager_id: z.string().trim(),
})

type FormValues = z.infer<typeof schema>

/** Create and edit in one dialog, following the `JobPostingFormDialog` shape. `code` is
 *  immutable once set -- the API's `LocationUpdate` schema has no field for it -- so it is
 *  editable only while creating. */
export function LocationFormDialog({
  open,
  location,
  onClose,
}: {
  open: boolean
  location?: StockLocation
  onClose: () => void
}) {
  const isEdit = location !== undefined
  const queryClient = useQueryClient()
  const can = useAuth((s) => s.can)
  const [formError, setFormError] = useState<string | null>(null)

  // `users:read` is admin/finance/hr only in the PRD matrix -- an inventory manager, who
  // otherwise has full control of locations, does not hold it. Rather than 403 on open,
  // the manager picker only loads (and only shows) for callers who can actually see users.
  const canPickManager = can('users', 'read')
  const users = useQuery({
    queryKey: ['users', 1],
    queryFn: () => listUsers(1),
    retry: false,
    enabled: open && canPickManager,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: location?.code ?? '',
      name: location?.name ?? '',
      type: (location?.type as (typeof LOCATION_TYPES)[number]) ?? 'store',
      status: (location?.status as (typeof LOCATION_STATUSES)[number]) ?? 'active',
      address: location?.address ?? '',
      city: location?.city ?? '',
      state: location?.state ?? '',
      postal_code: location?.postal_code ?? '',
      phone: location?.phone ?? '',
      latitude: location?.latitude ?? '',
      longitude: location?.longitude ?? '',
      manager_id: location?.manager_id ?? '',
    },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const shared = {
        name: values.name,
        type: values.type,
        status: values.status,
        address: values.address || null,
        city: values.city || null,
        state: values.state || null,
        postal_code: values.postal_code || null,
        phone: values.phone || null,
        latitude: values.latitude ? Number(values.latitude) : null,
        longitude: values.longitude ? Number(values.longitude) : null,
        manager_id: values.manager_id || null,
      }
      return location
        ? updateLocation(location.id, shared)
        : createLocation({ ...shared, code: values.code })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['stock-locations'] })
      toast.success(isEdit ? 'Location saved.' : 'Location added.')
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(
        error instanceof ApiError ? error.message : 'Could not reach the server. Try again.',
      )
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? `Edit ${location.name}` : 'New location'}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="location-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Add location'}
            </Button>
          </>
        }
      >
        <form id="location-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Code"
              error={errors.code?.message}
              hint={isEdit ? 'Fixed once created.' : 'e.g. ST-ANDHERI'}
            >
              {(props) => (
                <Input {...props} {...register('code')} autoComplete="off" disabled={isEdit} />
              )}
            </Field>
            <Field label="Name" error={errors.name?.message}>
              {(props) => <Input {...props} {...register('name')} autoComplete="off" />}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Type" error={errors.type?.message}>
              {(props) => (
                <select {...props} {...register('type')} className={SELECT_CLASS}>
                  {LOCATION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {LOCATION_TYPE_LABELS[type]}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Status" error={errors.status?.message}>
              {(props) => (
                <select {...props} {...register('status')} className={SELECT_CLASS}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              )}
            </Field>
          </div>

          <Field
            label="Address"
            error={errors.address?.message}
            hint="Optional. With city, state, PIN code and phone, couriers can collect online orders from here."
          >
            {(props) => <Input {...props} {...register('address')} autoComplete="off" />}
          </Field>

          <div className="grid grid-cols-3 gap-4">
            <Field label="City" error={errors.city?.message}>
              {(props) => <Input {...props} {...register('city')} autoComplete="off" />}
            </Field>
            <Field label="State" error={errors.state?.message}>
              {(props) => <Input {...props} {...register('state')} autoComplete="off" />}
            </Field>
            <Field label="PIN code" error={errors.postal_code?.message}>
              {(props) => (
                <Input
                  {...props}
                  {...register('postal_code')}
                  autoComplete="off"
                  inputMode="numeric"
                />
              )}
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Phone"
              error={errors.phone?.message}
              hint="Optional. A 10-digit mobile: the courier calls it at pickup."
            >
              {(props) => <Input {...props} {...register('phone')} autoComplete="off" />}
            </Field>
            {canPickManager && (
              <Field label="Manager" error={errors.manager_id?.message} hint="Optional.">
                {(props) => (
                  <select
                    {...props}
                    {...register('manager_id')}
                    className={SELECT_CLASS}
                    disabled={users.isLoading}
                  >
                    <option value="">No manager</option>
                    {(users.data?.data ?? []).map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Latitude" error={errors.latitude?.message} hint="Optional, -90 to 90.">
              {(props) => <Input {...props} {...register('latitude')} type="number" step="any" />}
            </Field>
            <Field
              label="Longitude"
              error={errors.longitude?.message}
              hint="Optional, -180 to 180."
            >
              {(props) => <Input {...props} {...register('longitude')} type="number" step="any" />}
            </Field>
          </div>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
